import { connect, getIn } from 'formik';
import { find } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';

import ProgressBar from 'hb-react/backoffice/components/ProgressBar';
import { getEqualAmounts } from 'hb-react/shared/apps/Donations/utils/gifts';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import { withFeatureFlag } from 'hb-react/shared/hooks/useFeatureFlag';
import { numberWithCurrency } from 'hb-react/shared/utils/formatNumber';

import AmountError from './AmountError';
import Amounts from './Amounts';
import FundsSection from './FundsSection';
import HEP from './HEP';
import OtherAmount from './OtherAmount';
import Recurring from './Recurring';
import SectionTitle from '../SectionTitle';

class AmountsAndFundsSections extends Component {
  state = {
    selectedAmount: null,
    otherAmount: '',
  };

  // LIFECYCLE

  componentDidMount() {
    const {
      initialAmount,
      campaign: { amounts },
    } = this.props;

    if (initialAmount) {
      const matchingAmount = find(
        amounts,
        (amount) => Number(amount) === Number(initialAmount),
      );

      if (matchingAmount) {
        this.handleSelectAmount(matchingAmount);
      } else {
        this.handleOtherAmountChange(initialAmount);
      }
    }
  }

  // LOGIC

  setGlobalAmount = ({ newAmount, shouldSplitGiftsEqually }) => {
    const { formik } = this.props;
    const gifts = getIn(formik.values, 'donation_order[gifts_attributes]');

    formik.setFieldValue('donation_order[amount]', newAmount);

    if (shouldSplitGiftsEqually) {
      this.setAmountsEqually({
        giftCount: gifts.length,
        totalAmount: newAmount,
      });
    }
  };

  updateGlobalAmountFromGifts = (newAmount) => {
    this.setState({
      selectedAmount: null,
      otherAmount: newAmount,
    });
    this.setGlobalAmount({ newAmount, shouldSplitGiftsEqually: false });
  };

  setGiftAmount = ({ amount, giftId }) => {
    const { formik } = this.props;

    formik.setFieldValue(
      `donation_order[gifts_attributes][${giftId}][amount]`,
      amount,
    );
  };

  setGiftCustomFund = ({ customFund, giftId }) => {
    const { formik } = this.props;

    formik.setFieldValue(
      `donation_order[gifts_attributes][${giftId}][custom_fund]`,
      customFund,
    );
  };

  setAmountsEqually = ({ giftCount, totalAmount }) => {
    const equalAmounts = getEqualAmounts({
      totalAmount,
      giftCount,
    });

    equalAmounts.forEach((amount, index) => {
      this.setGiftAmount({ amount, giftId: index });
    });
  };

  // HANDLERS

  handleSelectAmount = (newAmount) => {
    const { formik } = this.props;

    this.setState({
      selectedAmount: newAmount,
      otherAmount: '',
    });
    formik.setFieldTouched('donation_order[amount]', true, false);
    this.setGlobalAmount({ newAmount, shouldSplitGiftsEqually: true });
  };

  handleOtherAmountChange = (newAmount) => {
    this.setState({
      selectedAmount: null,
      otherAmount: newAmount,
    });
    this.setGlobalAmount({ newAmount, shouldSplitGiftsEqually: true });
  };

  handleOtherAmountBlur = () => {
    const { formik } = this.props;

    formik.setFieldTouched('donation_order[amount]', true, false);
  };

  // RENDERERS

  render() {
    const { campaign, periodicities, advancedOptionsEnabled } = this.props;
    const { selectedAmount, otherAmount } = this.state;

    return (
      <>
        <Card mb={5} position="relative" noBorderRadiusOnSmallScreen>
          {campaign.enable_progress_bar && campaign.goal_amount > 0 && (
            <Box
              display="block"
              px={5}
              pt={4}
              pb={10}
              data-testid="donation-progressbar"
            >
              <ProgressBar
                total={campaign.goal_amount}
                current={campaign.total_amount || 0}
                startText={numberWithCurrency(0, campaign.currency.code)}
                currentText={numberWithCurrency(
                  campaign.total_amount,
                  campaign.currency.code,
                )}
                endText={numberWithCurrency(
                  campaign.goal_amount,
                  campaign.currency.code,
                )}
              />
            </Box>
          )}
          <Description
            display="block"
            mb={10}
            className="ckeditor-content--reset"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: campaign.description }}
          />

          <Box display="block" mb={5}>
            <SectionTitle
              title={s__('Donations|Gift amount')}
              subTitle={s__('Donations|Amount is in %{currency}').i__({
                currency: campaign.currency.code,
              })}
            />

            <RequiredExplanation />
          </Box>

          <Recurring campaign={campaign} periodicities={periodicities} />

          <div style={{ display: 'inline-block' }}>
            <Amounts
              campaign={campaign}
              selectedAmount={selectedAmount}
              onSelect={this.handleSelectAmount}
            />
            <OtherAmount
              campaign={campaign}
              otherAmount={otherAmount}
              name="other_amount"
              onChange={this.handleOtherAmountChange}
              onBlur={this.handleOtherAmountBlur}
              biggerDisplay
            />
            <AmountError
              minimumAmount={campaign.minimum_amount}
              currency={campaign.currency}
            />
          </div>
        </Card>

        {advancedOptionsEnabled &&
          (campaign.funds.length > 1 || campaign.enable_hep_companies) && (
            <Card mb={5} position="relative">
              {campaign.funds.length > 1 && (
                <FundsSection
                  campaign={campaign}
                  setGiftAmount={this.setGiftAmount}
                  setGiftCustomFund={this.setGiftCustomFund}
                  setAmountsEqually={this.setAmountsEqually}
                  updateGlobalAmountFromGifts={this.updateGlobalAmountFromGifts}
                />
              )}
              {campaign.enable_hep_companies && <HEP />}
            </Card>
          )}
      </>
    );
  }
}

AmountsAndFundsSections.propTypes = {
  campaign: PropTypes.object.isRequired,
  advancedOptionsEnabled: PropTypes.bool.isRequired,
  initialAmount: PropTypes.string,
  periodicities: PropTypes.array,
};

export default connect(withFeatureFlag(AmountsAndFundsSections));

const Description = styled(Box)`
  font-size: 1.3em;
  font-weight: 200;
  line-height: 1.2;
`;
